import { Controller } from "@hotwired/stimulus"

// Self-storage software savings calculator. Estimates annual labor savings,
// recovered delinquent rent, and software cost change, plus a rough payback
// period. Pure arithmetic, recomputed live. Illustrative only — see disclaimer.
export default class extends Controller {
  static targets = [
    "facilities", "units", "occupancy", "rent", "currentSoftware", "newSoftware",
    "hours", "wage", "automation", "delinquency",
    "occLabel", "autoLabel", "delinqLabel",
    "labor", "delinqRecovery", "softwareDelta", "perUnit",
    "netAnnual", "netMonthly", "payback"
  ]

  connect() { this.compute() }

  compute() {
    const facilities = Math.max(1, this.#num(this.facilitiesTarget))
    const units = Math.max(0, this.#num(this.unitsTarget))
    const occ = this.#clamp(this.#num(this.occupancyTarget), 0, 100)
    const rent = Math.max(0, this.#num(this.rentTarget))
    const currentSoftMonthly = Math.max(0, this.#num(this.currentSoftwareTarget))
    const newSoftPerFacMonthly = Math.max(0, this.#num(this.newSoftwareTarget))
    const hours = Math.max(0, this.#num(this.hoursTarget))
    const wage = Math.max(0, this.#num(this.wageTarget))
    const automation = this.#clamp(this.#num(this.automationTarget), 0, 90)
    const delinqPts = this.#clamp(this.#num(this.delinquencyTarget), 0, 10)

    if (this.hasOccLabelTarget) this.occLabelTarget.textContent = Math.round(occ)
    if (this.hasAutoLabelTarget) this.autoLabelTarget.textContent = Math.round(automation)
    if (this.hasDelinqLabelTarget) this.delinqLabelTarget.textContent = Math.round(delinqPts)

    const currentSoftwareAnnual = currentSoftMonthly * 12
    const newSoftwareAnnual = newSoftPerFacMonthly * facilities * 12
    const softwareCostDelta = currentSoftwareAnnual - newSoftwareAnnual // + = save on software

    const hoursSavedPerWeek = hours * (automation / 100)
    const laborSavingsAnnual = hoursSavedPerWeek * 52 * wage

    const occupiedUnits = units * (occ / 100)
    const grossMonthlyRent = occupiedUnits * rent
    const delinquencyRecoveryAnnual = grossMonthlyRent * (delinqPts / 100) * 12

    const netAnnualSavings = laborSavingsAnnual + delinquencyRecoveryAnnual + softwareCostDelta
    const netMonthlySavings = netAnnualSavings / 12
    const savingsPerUnit = units > 0 ? netAnnualSavings / units : 0

    this.laborTarget.textContent = this.#money(laborSavingsAnnual)
    this.delinqRecoveryTarget.textContent = this.#money(delinquencyRecoveryAnnual)
    this.softwareDeltaTarget.textContent = this.#signedMoney(softwareCostDelta)
    this.perUnitTarget.textContent = this.#money(savingsPerUnit)
    this.netAnnualTarget.textContent = this.#signedMoney(netAnnualSavings)
    this.netMonthlyTarget.textContent = this.#signedMoney(netMonthlySavings)

    const newSoftwareAnnualCost = newSoftPerFacMonthly * facilities
    if (netMonthlySavings <= 0) {
      this.paybackTarget.textContent = "Does not pay back at these inputs"
    } else {
      const months = newSoftwareAnnualCost / netMonthlySavings
      this.paybackTarget.textContent = months <= 1 ? "About 1 month or less" : `About ${Math.round(months)} months`
    }
  }

  #num(el) { const v = parseFloat(el.value); return Number.isNaN(v) ? 0 : v }
  #clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)) }
  #money(n) {
    return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
  }
  #signedMoney(n) {
    const s = this.#money(Math.abs(n))
    return n < 0 ? `-${s}` : s
  }
}
